/**
 * Evicts what an external-email sender grant let its holder load once the
 * grant is revoked, wherever in the app the holder is. A grant is revoked by an
 * UPDATE stamping `revoked_at` (the only change the table allows) and removed
 * only by the organization cascade. The grantee read policy keeps a revoked row
 * readable, so the UPDATE is delivered; REPLICA IDENTITY FULL puts the owner in
 * `old` on a DELETE.
 *
 * Mounted at app root through {@link ExternalSenderGrantRealtimeMount}, beside
 * the share-access provider that does the same for correspondence shares.
 */
import { useEffect, useEffectEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { pastedEmailEarlierMessagesKeys } from '@/hooks/correspondence/use-pasted-email-earlier-messages';
import { readShareRevokedAt, readShareString } from '@/hooks/share-access/share-payload-utils';
import { correspondenceDraftsRootKey } from '@/hooks/email-drafts-correspondence-keys';
import { useOrganizationFeatureAccess } from '@/hooks/use-feature-gates';
import { useRealtimeChannel } from '@/hooks/use-realtime-channel';
import { queryKeyMatchesAnyPrefix } from '@/lib/share-gated-cache-registry';

import { externalEmailSendKeys } from './query-keys';

import type { QueryClient } from '@tanstack/react-query';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import type { QueryKeyPrefix } from '@/lib/share-gated-cache-registry';

const GRANTS_TABLE = 'external_email_sender_grants';

/** The reply drafts, the pasted thread they answer, and the send panels' read models. */
const SENDER_GRANT_GATED_PREFIXES: readonly QueryKeyPrefix[] = [
  correspondenceDraftsRootKey,
  pastedEmailEarlierMessagesKeys.all,
  externalEmailSendKeys.states(),
];

const SENDER_LISTS_KEY = [...externalEmailSendKeys.all, 'senders'] as const;

type Payload = RealtimePostgresChangesPayload<Record<string, unknown>>;

/** `revokedMailboxId` is undefined when the payload did not name the mailbox. */
type SenderGrantRevocationListener = (revokedMailboxId: string | undefined) => void;

// A module Set rather than a callback handed to the channel, so a composer
// registering or unregistering never churns the realtime subscription.
const revocationListeners = new Set<SenderGrantRevocationListener>();

function notifySenderGrantRevoked(revokedMailboxId: string | undefined): void {
  for (const listener of revocationListeners) {
    listener(revokedMailboxId);
  }
}

/** The mailbox whose grant the revocation payload takes away, when it names one. */
function readRevokedSenderGrantMailboxId(payload: Payload): string | undefined {
  const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
  return readShareString(row, 'external_email_mailbox_id');
}

/**
 * Runs `onRevoked` with the revoked grant's mailbox whenever the signed-in
 * user's own sender grant is revoked.
 * The cache eviction cannot reach state a mounted composer already copied out
 * of the cache; this is how that composer hears it must drop it.
 */
export function useSenderGrantRevocationEvents(onRevoked: SenderGrantRevocationListener): void {
  const handleRevoked = useEffectEvent(onRevoked);
  useEffect(() => {
    const listener: SenderGrantRevocationListener = (revokedMailboxId) =>
      handleRevoked(revokedMailboxId);
    revocationListeners.add(listener);
    return () => {
      revocationListeners.delete(listener);
    };
  }, []);
}

/** True when the payload takes a grant away from `userId`. */
export function isOwnSenderGrantRevocation(payload: Payload, userId: string): boolean {
  if (payload.table !== GRANTS_TABLE) {
    return false;
  }
  if (payload.eventType === 'UPDATE') {
    return (
      readShareString(payload.new, 'user_id') === userId &&
      typeof readShareRevokedAt(payload.new) === 'string'
    );
  }
  if (payload.eventType === 'DELETE') {
    return readShareString(payload.old, 'user_id') === userId;
  }
  return false;
}

function invalidateSenderLists(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: SENDER_LISTS_KEY }).catch(() => {
    /* best-effort; the senders dialog asks again on open */
  });
}

/**
 * Drops every cache the grant let its holder read. `resetQueries`, not
 * `removeQueries`: an open panel's observer would keep rendering the rows it
 * already holds, where a reset blanks it at once and re-reads under RLS.
 */
export function evictSenderGrantCaches(queryClient: QueryClient): void {
  queryClient
    .resetQueries({
      predicate: ({ queryKey }) => queryKeyMatchesAnyPrefix(queryKey, SENDER_GRANT_GATED_PREFIXES),
    })
    .catch(() => {
      /* eviction is best-effort; the next read re-derives from RLS regardless */
    });
  invalidateSenderLists(queryClient);
}

/** A revocation missed while the socket was down: re-read the same surfaces under RLS. */
export function reconcileSenderGrantCaches(queryClient: QueryClient): void {
  queryClient
    .invalidateQueries({
      predicate: ({ queryKey }) => queryKeyMatchesAnyPrefix(queryKey, SENDER_GRANT_GATED_PREFIXES),
    })
    .catch(() => {
      /* best-effort; the next mount refetches */
    });
  invalidateSenderLists(queryClient);
}

export function useSenderGrantRevocationRealtime(
  userId: string | undefined,
  organizationId: string | undefined,
): void {
  const queryClient = useQueryClient();
  const { data: featureAccess } = useOrganizationFeatureAccess(organizationId);
  const enabled = Boolean(userId) && featureAccess?.email_external_channel === true;
  const filter = `user_id=eq.${userId ?? ''}`;

  useRealtimeChannel({
    channelName: userId ? `external-sender-grants-${userId}` : 'external-sender-grants',
    subscriptions: enabled
      ? [
          { event: 'UPDATE', table: GRANTS_TABLE, filter },
          { event: 'DELETE', table: GRANTS_TABLE, filter },
        ]
      : [],
    onPayload: (payload: Payload) => {
      // Payloads are not generation-guarded across a user switch on a shared
      // workstation; drop anything not addressed to the current user.
      if (userId && isOwnSenderGrantRevocation(payload, userId)) {
        evictSenderGrantCaches(queryClient);
        notifySenderGrantRevoked(readRevokedSenderGrantMailboxId(payload));
      }
    },
    onResubscribed: enabled ? () => reconcileSenderGrantCaches(queryClient) : undefined,
    enabled,
  });
}

export function ExternalSenderGrantRealtimeMount(): null {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  useSenderGrantRevocationRealtime(user?.id, currentOrganization?.id);
  return null;
}
